import { Component, computed, effect, inject, input, output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { GetOpportunityActualValuesResultDto, GetOpportunityTermResultDto } from '@api-proxies';
import { OpportunityTermValue } from '@app/models/OpportunityTermValue';
import { EntityCardSection } from '@enums/EntityCardSection';
import { AppEditorFactory } from '@factories/app-editor-factory/app-editor-factory';
import { EditorType } from '@factories/app-editor-factory/enum/editor-type';
import { EntityTypeCustomFieldService } from '@http/clients/entity-type-custom-field.service';
import { OpportunityService } from '@http/clients/opportunity.service';
import { CompanyPositions } from '@models/enums/CompanyPositions';
import { ContactPositions } from '@models/enums/ContactPositions';
import { EntityTypes } from '@models/enums/EntityTypes';
import { Opportunity } from '@models/Opportunity';
import { OpportunityActualValue } from '@models/OpportunityActualValue';
import { EntityComponentType } from '@shared/card-editors/entity-editor/entity-enums/entity';
import { customFieldPlacement, ICardConfig } from '@utils/custom-field-placement';
import { GenericCardComponent, ValidatorType } from 'ngx-lib';
import { OpportunitiesValuesGridComponent } from './opportunities-values-grid/opportunities-values-grid.component';

@Component({
    selector: 'app-opportunities-single-overview',
    templateUrl: './opportunities-single-overview.component.html',
    styleUrls: ['./opportunities-single-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [GenericCardComponent, OpportunitiesValuesGridComponent]
})
export class OpportunitiesSingleOverviewComponent {
    @ViewChild('mainInfoCard')
    mainInfoCard?: GenericCardComponent;

    @ViewChild('organizationCard')
    organizationCard?: GenericCardComponent;

    @ViewChild('buildingCard')
    buildingCard?: GenericCardComponent;

    @ViewChild('bidInfoCard')
    bidInfoCard?: GenericCardComponent;

    @ViewChild('detailsCard')
    detailsCard?: GenericCardComponent;

    @ViewChild('customInfoCard')
    customInfoCard?: GenericCardComponent;

    @ViewChild('valuesGrid')
    valuesGrid?: OpportunitiesValuesGridComponent;

    isReadOnly = input<boolean>(false);
    isCreateEstimate = input<boolean>(false);
    actualValue = input<GetOpportunityActualValuesResultDto>(new OpportunityActualValue());
    termValue = input<GetOpportunityTermResultDto>(new OpportunityTermValue());
    baseEstimateExternal = input<boolean>(false);
    opportunityInput = input<Opportunity | undefined>(undefined);

    valueChange = output<boolean>();

    factory: AppEditorFactory = new AppEditorFactory();

    entityCardSection: typeof EntityCardSection = EntityCardSection;
    cardsConfig: ICardConfig = [];

    readonly opportunity = computed<Opportunity>(() => {
        const inputOpportunity = this.opportunityInput();
        if (inputOpportunity) {
            return inputOpportunity;
        }
        const opportunityView = this.opportunityService.opportunity();
        return opportunityView ? Opportunity.fromInterface(opportunityView) : new Opportunity();
    });

    // Properties to track archived items in each card
    organizationHasArchived = false;
    buildingHasArchived = false;
    customerHasArchived = false;
    contractingTeamHasArchived = false;
    bidInfoHasArchived = false;
    productTypeHasArchived = false;
    usersHasArchived = false;

    private readonly entityCustomFieldService = inject(EntityTypeCustomFieldService);
    private readonly opportunityService = inject(OpportunityService);

    private readonly cardsConfigEffect = effect(async () => {
        this.opportunity();
        this.isCreateEstimate();
        this.isReadOnly();

        await this.initializeCardsConfig();
    });

    validate(): boolean {
        const mainInfoCardValid = this.mainInfoCard ? this.mainInfoCard.validate() : false;
        const organizationCardValid = this.organizationCard ? this.organizationCard.validate() : false;
        const buildingCardValid = this.buildingCard ? this.buildingCard.validate() : false;
        const bidInfoCard = this.bidInfoCard ? this.bidInfoCard.validate() : false;
        const detailsCardValid = this.detailsCard ? this.detailsCard.validate() : false;
        const customInfoCard = this.customInfoCard ? this.customInfoCard.validate() : true;
        const valuesGridValid = this.valuesGrid ? this.valuesGrid.validate() : true;

        const allValid =
            mainInfoCardValid &&
            organizationCardValid &&
            buildingCardValid &&
            bidInfoCard &&
            detailsCardValid &&
            customInfoCard &&
            valuesGridValid;

        return allValid;
    }

    clearValidationErrors(): void {
        this.mainInfoCard?.clearValidationErrors();
        this.organizationCard?.clearValidationErrors();
        this.buildingCard?.clearValidationErrors();
        this.bidInfoCard?.clearValidationErrors();
        this.detailsCard?.clearValidationErrors();
        this.valuesGrid?.clearValidationErrors();
    }

    scrollToFirstErrorElement(): void {
        // Use requestAnimationFrame for better performance
        requestAnimationFrame(() => {
            document.querySelectorAll('.error')[0]?.scrollIntoView({ behavior: 'smooth' });
        });
    }

    emitValueChanged(): void {
        this.valueChange.emit(true);
    }

    private async initializeCardsConfig(): Promise<void> {
        const opportunity = this.opportunity();
        const isReadOnly = this.isReadOnly();
        const isCreateEstimate = this.isCreateEstimate();

        this.cardsConfig = {
            [EntityCardSection.OpportunityMainInfo]: [
                {
                    label: 'Name',
                    componentId: EditorType.textInput,
                    fieldName: 'name',
                    bindContext: opportunity,
                    validations: [
                        { type: ValidatorType.required },
                        { type: ValidatorType.string, args: { maxLength: 200 } }
                    ],
                    isReadOnly: isReadOnly,
                    additionalInfo: {
                        shouldTrimSpaces: true
                    }
                },
                {
                    label: 'Due Date & Time',
                    componentId: EditorType.date,
                    fieldName: 'dueDate',
                    bindContext: opportunity,
                    isReadOnly: isReadOnly,
                    additionalInfo: { timeEnabled: true },
                    validations: [{ apply: isCreateEstimate, type: ValidatorType.required }]
                },
                {
                    label: 'Expected Booking Date',
                    componentId: EditorType.date,
                    fieldName: 'expectedResponseDate',
                    bindContext: opportunity,
                    isReadOnly: isReadOnly,
                    additionalInfo: { timeEnabled: true }
                },
                {
                    label: 'Project ID',
                    componentId: EditorType.textInput,
                    fieldName: 'projectId',
                    bindContext: opportunity,
                    isReadOnly: isReadOnly,
                    validations: [{ type: ValidatorType.string, args: { maxLength: 25 } }]
                },
                {
                    label: 'Description',
                    componentId: EditorType.textarea,
                    fieldName: 'description',
                    bindContext: opportunity,
                    isReadOnly: isReadOnly,
                    validations: [{ type: ValidatorType.string, args: { maxLength: 1000 } }]
                }
            ],
            [EntityCardSection.OpportunityOrganization]: [
                {
                    label: 'Organization',
                    componentId: EditorType.textInput,
                    fieldName: 'organizationName',
                    bindContext: this.opportunity(),
                    isReadOnly: true
                },
                {
                    label: 'Department',
                    componentId: EditorType.department,
                    fieldName: 'divisionId',
                    bindContext: opportunity,
                    isReadOnly: isReadOnly,
                    additionalInfo: { isActive: true },
                    validations: [{ apply: isCreateEstimate, type: ValidatorType.required }]
                }
            ],
            [EntityCardSection.OpportunityBuilding]: [
                {
                    componentId: EditorType.building,
                    fieldName: 'buildingId',
                    bindContext: opportunity,
                    isReadOnly: isReadOnly
                }
            ],
            [EntityCardSection.OpportunityCustomer]: [
                {
                    label: 'Company',
                    componentId: EditorType.company,
                    fieldName: 'companyId',
                    bindContext: opportunity,
                    isReadOnly: isReadOnly,
                    // this property 'additionalInfo: { allowAction: true }' must be set if we want to allow the user to create a new company by opening quick add modal
                    additionalInfo: { allowAction: true, isActive: true }
                },
                {
                    label: 'Company Role',
                    componentId: EditorType.entity,
                    fieldName: 'opportunityBuildingContactRoles',
                    entityId: 'opportunityBuildingContactRole',
                    bindContext: opportunity,
                    isReadOnly: isReadOnly,
                    additionalInfo: {
                        entityType: EntityTypes.Company
                    }
                },
                {
                    label: 'Contacts',
                    componentId: EditorType.opportunityContact,
                    fieldName: 'opportunityContacts',
                    bindContext: this.opportunity(),
                    additionalInfo: ContactPositions.Customer,
                    isReadOnly: this.isReadOnly()
                }
            ],
            [EntityCardSection.OpportunityDetails]: [
                {
                    label: 'Salesperson',
                    componentId: EditorType.opportunityUser,
                    fieldName: 'opportunitySalespersons',
                    bindContext: opportunity,
                    isReadOnly: isReadOnly,
                    validations: [{ apply: isCreateEstimate, type: ValidatorType.required }]
                },
                {
                    label: 'Probability',
                    componentId: EditorType.opportunityProbability,
                    fieldName: 'opportunityProbabilityId',
                    bindContext: opportunity,
                    isReadOnly: isReadOnly,
                    validations: [{ apply: isCreateEstimate, type: ValidatorType.required }]
                },
                {
                    label: 'Opportunity Source',
                    componentId: EditorType.leadSource,
                    fieldName: 'leadSourceId',
                    bindContext: opportunity,
                    isReadOnly: isReadOnly
                },
                {
                    label: 'Markets',
                    componentId: EditorType.entity,
                    fieldName: 'opportunityMarkets',
                    bindContext: this.opportunity(),
                    entityId: 'opportunityMarket',
                    isReadOnly: isReadOnly,
                    validations: [{ apply: isCreateEstimate, type: ValidatorType.required }]
                },
                {
                    label: 'Product Types',
                    componentId: EditorType.entity,
                    fieldName: 'opportunityProductTypes',
                    entityId: 'opportunityProductType',
                    bindContext: this.opportunity(),
                    additionalInfo: {
                        entityType: EntityTypes.Opportunity
                    },
                    isReadOnly: this.isReadOnly()
                },
                {
                    label: 'Tags',
                    componentId: EditorType.entity,
                    fieldName: 'opportunityTags',
                    bindContext: this.opportunity(),
                    entityId: 'opportunityTag',
                    isReadOnly: isReadOnly,
                    additionalInfo: {
                        entityType: EntityTypes.Opportunity,
                        entityComponentType: EntityComponentType.typeAheadMultiselect,
                        allowToSaveNewItem: true
                    }
                },
                {
                    label: 'Project Duration (days)',
                    componentId: EditorType.numberInput,
                    fieldName: 'projectDuration',
                    bindContext: opportunity,
                    isReadOnly: isReadOnly
                },
                {
                    label: 'Project Start',
                    componentId: EditorType.date,
                    fieldName: 'projectStart',
                    bindContext: opportunity,
                    isReadOnly: isReadOnly
                },
                {
                    label: 'Project End',
                    componentId: EditorType.date,
                    fieldName: 'projectEnd',
                    bindContext: opportunity,
                    isReadOnly: isReadOnly
                }
            ],
            [EntityCardSection.OpportunityBidInfo]: [
                {
                    label: 'Competing Bidders',
                    componentId: EditorType.opportunityCompany,
                    fieldName: 'opportunityCompanies',
                    bindContext: this.opportunity(),
                    additionalInfo: CompanyPositions.CompetingBidder,
                    isReadOnly: this.isReadOnly()
                },
                {
                    label: 'Recipients',
                    componentId: EditorType.opportunityCompany,
                    fieldName: 'opportunityCompanies',
                    bindContext: this.opportunity(),
                    additionalInfo: CompanyPositions.BidRecipient,
                    isReadOnly: this.isReadOnly()
                },
                {
                    label: 'Categories',
                    componentId: EditorType.entity,
                    fieldName: 'opportunityBidCategories',
                    bindContext: this.opportunity(),
                    entityId: 'opportunityBidCategory',
                    isReadOnly: this.isReadOnly(),
                    validations: [{ apply: isCreateEstimate, type: ValidatorType.required }]
                }
            ],
            [EntityCardSection.OpportunityContractingTeam]: [
                {
                    label: 'Architect',
                    componentId: EditorType.opportunityCompany,
                    fieldName: 'opportunityCompanies',
                    bindContext: this.opportunity(),
                    additionalInfo: CompanyPositions.Architect,
                    isReadOnly: this.isReadOnly()
                },
                {
                    label: 'Consulting Engineer',
                    componentId: EditorType.opportunityCompany,
                    fieldName: 'opportunityCompanies',
                    bindContext: this.opportunity(),
                    additionalInfo: CompanyPositions.ConsultingEngineer,
                    isReadOnly: this.isReadOnly()
                },
                {
                    label: 'Prime Contractor',
                    componentId: EditorType.opportunityCompany,
                    fieldName: 'opportunityCompanies',
                    bindContext: this.opportunity(),
                    additionalInfo: CompanyPositions.PrimeContractor,
                    isReadOnly: this.isReadOnly()
                },
                {
                    label: 'Contract With',
                    componentId: EditorType.opportunityCompany,
                    fieldName: 'opportunityCompanies',
                    bindContext: this.opportunity(),
                    additionalInfo: CompanyPositions.ContractWith,
                    isReadOnly: this.isReadOnly()
                }
            ],
            [EntityCardSection.OpportunityCustomInfo]: []
        };

        if (!opportunity.customFieldValues?.length)
            opportunity.customFieldValues = await this.entityCustomFieldService.getCustomFieldsByEntityType(
                EntityTypes.Opportunity
            );

        opportunity.customFieldValues.forEach((customField) => {
            customFieldPlacement(this.cardsConfig, customField, EntityCardSection.OpportunityCustomInfo, isReadOnly);
        });

        await this.checkArchivedItems(opportunity);
    }

    private async checkArchivedItems(opportunity: Opportunity): Promise<void> {
        this.organizationHasArchived = opportunity.divisionIsActive === false;
        this.buildingHasArchived = opportunity.buildingIsActive === false;
        this.productTypeHasArchived = opportunity.opportunityProductTypes?.some((c) => c.isActive === false) ?? false;
        this.usersHasArchived = opportunity.opportunitySalespersons?.some((c) => c.isActive === false) ?? false;
        const hasArchivedCompany =
            opportunity.opportunityCompanies?.some(
                (c) => !c.isActive && c.opportunityCompanyRoles?.some((r) => r.buildingContactRoleName === 'Customer')
            ) ?? false;

        const hasArchivedContact =
            opportunity.opportunityContacts?.some(
                (c) =>
                    !c.isActive &&
                    c.opportunityContactBuildingContactRoles?.some(
                        (role) => role.buildingContactRoleName === 'Customer'
                    )
            ) ?? false;

        this.customerHasArchived = hasArchivedCompany || hasArchivedContact;

        const contractingTeamPositions = [
            CompanyPositions.Architect,
            CompanyPositions.ConsultingEngineer,
            CompanyPositions.PrimeContractor,
            CompanyPositions.ContractWith
        ];
        this.contractingTeamHasArchived = this.hasArchivedCompanyByRoles(opportunity, contractingTeamPositions);

        const bidInfoPositions = [CompanyPositions.CompetingBidder, CompanyPositions.BidRecipient];
        this.bidInfoHasArchived = this.hasArchivedCompanyByRoles(opportunity, bidInfoPositions);
    }

    private hasArchivedCompanyByRoles(opportunity: Opportunity, roleIds: number[]): boolean {
        return (
            opportunity.opportunityCompanies?.some(
                (company) =>
                    company.isActive === false &&
                    company.opportunityCompanyRoles?.some((role) => roleIds.includes(role.buildingContactRoleId))
            ) ?? false
        );
    }
}
